import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { OccupancyType } from '@yard/app/api'
import { resolveOccupancySchemeColor } from '@yard/utils/occupancies.utils'
import { observer } from 'mobx-react-lite'
import { ReactNode, useMemo } from 'react'
import { CONTAINER_DETAILS_WIDTH } from '../../helpers/yard-view.utils'
import {
  isAccumulatorEligible,
  useAccumulatorClickHandler,
} from '../../hooks/use-accumulator-click-handler'
import { HousekeepingPlanAccumulatorViewStore } from '../../stores/housekeeping-plan-accumulator.view-store'
import { YardViewStore } from '../../stores/yard.view-store'

interface Props {
  viewStore: YardViewStore
  housekeepingPlanAccumulatorViewStore: HousekeepingPlanAccumulatorViewStore
  renderContainerDetailsByJourneyId: (
    journeyId: number,
    highlight?: boolean,
    isReserved?: boolean,
    schemeBackgroundColor?: string,
  ) => ReactNode
}
export const BlockContainerDetailsList = observer(
  ({
    viewStore,
    housekeepingPlanAccumulatorViewStore,
    renderContainerDetailsByJourneyId,
  }: Props) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const handleAccumulatorClick = useAccumulatorClickHandler(housekeepingPlanAccumulatorViewStore)
    const isAccumulating = housekeepingPlanAccumulatorViewStore.isAccumulatorOpen

    const activeScheme = viewStore.colorScheme
    const selectedSchemeSet = useMemo(
      () => new Set(viewStore.effectiveSelectedValuesForActiveScheme),
      [viewStore.effectiveSelectedValuesForActiveScheme],
    )
    const resolveSchemeColor = (occupancy: (typeof viewStore.blockOccupancies)[number]) =>
      resolveOccupancySchemeColor(occupancy, activeScheme, selectedSchemeSet)

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(1, ${CONTAINER_DETAILS_WIDTH}px)`,
          justifyContent: 'center',
          gap: theme.customSpacing.s,
          width: '100%',
        }}
      >
        {viewStore.blockOccupancies.length === 0 && (
          <Typography variant='body2'>
            {t('noContainersInThisBlock', 'No containers in this block')}
          </Typography>
        )}
        {viewStore.blockOccupancies.map(occupancy => {
          // Announced (Reserved) tiles are not clickable in accumulator mode —
          // the BE rejects them and we surface that as no affordance, not a dead
          // click + warning. See AC #11 and the spec in
          // .spec-announced-container-filter.md.
          const isClickable = isAccumulating && isAccumulatorEligible(occupancy)
          const isIneligibleInAccumulator = isAccumulating && !isAccumulatorEligible(occupancy)
          const isAccumulated =
            isAccumulating &&
            housekeepingPlanAccumulatorViewStore.isAccumulated(occupancy.containerId)
          // Position snapshot for L2.3 race-drop detection. focusedBlockId is
          // always set when blockOccupancies has entries (the right panel only
          // opens after a block has been focused), and Occupied tiles always
          // carry a position — the eligibility guard above filters Reserved.
          const blockId = viewStore.focusedBlockId
          const position = occupancy.position
          const onCellClick =
            isClickable && blockId && position
              ? () => handleAccumulatorClick({ ...occupancy, blockId, position })
              : undefined
          return (
            <Box
              key={`container-details-${occupancy.journeyId}`}
              onClick={onCellClick}
              onKeyDown={
                onCellClick
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onCellClick()
                      }
                    }
                  : undefined
              }
              role={onCellClick ? 'button' : undefined}
              tabIndex={onCellClick ? 0 : undefined}
              aria-disabled={isIneligibleInAccumulator || undefined}
              sx={{
                width: CONTAINER_DETAILS_WIDTH + 'px',
                cursor: isClickable
                  ? 'pointer'
                  : isIneligibleInAccumulator
                    ? 'not-allowed'
                    : undefined,
                outline: isAccumulated ? `2px solid ${theme.palette.primary.main}` : undefined,
                outlineOffset: isAccumulated ? '-2px' : undefined,
                borderRadius: isAccumulated ? theme.customRadius.medium : undefined,
              }}
            >
              {renderContainerDetailsByJourneyId(
                occupancy.journeyId,
                undefined,
                occupancy.type === OccupancyType.Reserved,
                resolveSchemeColor(occupancy),
              )}
            </Box>
          )
        })}
      </Box>
    )
  },
)
